import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { ShieldAlert, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

/**
 * Schuetzt eine Route.
 *
 * Der Ladezustand bekommt bewusst eine eigene Anzeige und leitet NICHT zur
 * Anmeldung: beim Neuladen ist das Access-Token immer weg, und das
 * Refresh-Cookie wird erst geprueft. Ohne diese Unterscheidung saehe jeder
 * Reload wie ein Rauswurf aus.
 */
export function ProtectedRoute({
  children,
  erfordert,
}: {
  children: ReactNode;
  erfordert?: string | string[];
}) {
  const { status, hatRecht } = useAuth();
  const ort = useLocation();

  if (status === 'laedt') {
    return (
      <div className="flex h-screen items-center justify-center text-gray-500">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
        <span>Sitzung wird geprüft…</span>
      </div>
    );
  }

  if (status !== 'angemeldet') {
    // Das Ziel mitgeben, damit der Benutzer nach der Anmeldung dort landet,
    // wo er hinwollte -- beim QR-Aufruf eines Elements ist genau das der
    // ganze Sinn des Links.
    const weiter = encodeURIComponent(ort.pathname + ort.search);
    return <Navigate to={`/login?weiter=${weiter}`} replace />;
  }

  const fehlend = erfordert
    ? [erfordert].flat().filter((s) => !hatRecht(s))
    : [];

  if (fehlend.length > 0) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <ShieldAlert className="mb-4 h-12 w-12 text-amber-500" />
        <h2 className="text-lg font-semibold text-gray-900">Keine Berechtigung</h2>
        <p className="mt-2 max-w-md text-sm text-gray-600">
          Für diese Seite fehlt Ihnen die Berechtigung{' '}
          <code className="rounded bg-gray-100 px-1">{fehlend.join(', ')}</code>.
          Wenden Sie sich an die Administration Ihres Unternehmens.
        </p>
      </div>
    );
  }

  return <>{children}</>;
}

export default ProtectedRoute;
